import { useState, type ReactNode } from "react";
import { eng } from "@/lib/engineApi";
import { useGrid } from "@/state/grid";
import { onRfiCreated } from "@/lib/events";
import { guardCloseout } from "@/lib/perms";

// RFI Register — faithful React port of vRFI() + its lifecycle handlers.
const scopeLabel = (s: string) => (s === "table" ? "📋 Table" : s === "station" ? "🔌 Station" : "🌐 Site-wide");

export default function RfiView() {
  const version = useGrid((s) => s.version);
  const bump = useGrid((s) => s.bump);
  const setView = useGrid((s) => s.setView);
  void version;

  const e = eng();
  const counts = e.rfiCounts();
  const filt = e.state.rfiFilter || "open";
  const all = e.rfiList();
  const filtered = all.filter((r: any) => {
    if (filt === "all") return true;
    if (filt === "open") return r.status !== "closed";
    return r.status === filt;
  });
  const sel = e.state.rfiSelected ? e.rfis[e.state.rfiSelected] : null;

  const setFilter = (k: string) => {
    e.state.rfiFilter = k;
    bump();
  };
  const select = (id: string) => {
    e.state.rfiSelected = id;
    e.state.rfiNew = false;
    bump();
  };
  const startNew = () => {
    e.state.rfiNew = true;
    e.state.rfiSelected = null;
    e.state.rfiNewScope = e.state.rfiNewScope || "table";
    bump();
  };

  const pills: [string, string, number][] = [
    ["open", "Open", counts.open],
    ["pending", "Pending", counts.pending],
    ["scheduled", "Scheduled", counts.scheduled],
    ["in_progress", "In progress", counts.in_progress],
    ["closed", "Closed", counts.closed],
    ["all", "All", counts.all],
  ];

  return (
    <div className="rfi-wrap">
      <div className="rfi-bar">
        <div className="rfi-title-row">
          <h2>Requests for Inspection</h2>
          <button className="obtn ok" onClick={startNew}>
            + Raise RFI
          </button>
        </div>
        <div className="cl-filter rfi-filter">
          {pills.map(([k, l, c]) => (
            <button key={k} className={`cl-pill ${filt === k ? "sel" : ""}`} onClick={() => setFilter(k)}>
              {l} <b>{c}</b>
            </button>
          ))}
        </div>
      </div>
      <div className="rfi-grid">
        <div className="rfi-list">
          {filtered.length ? (
            filtered.map((r: any) => {
              const st = e.RFI_STATUS[r.status];
              const oc = r.outcome ? e.RFI_OUTCOME[r.outcome] : null;
              return (
                <div
                  className={`rfi-card ${sel && sel.id === r.id ? "sel" : ""}`}
                  key={r.id}
                  onClick={() => select(r.id)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="rfi-card-head">
                    <span className="rfi-id mono">{r.id}</span>
                    <span className="rfi-status" style={{ ["--rc" as any]: st.color }}>
                      {st.glyph} {st.label}
                    </span>
                    {oc ? (
                      <span className="rfi-outcome" style={{ ["--rc" as any]: oc.color }}>
                        {oc.label}
                      </span>
                    ) : null}
                  </div>
                  <div className="rfi-card-title">{r.title}</div>
                  <div className="rfi-card-meta">
                    <span className="rfi-scope">{scopeLabel(r.scope)}</span>
                    <span className="rfi-target" dangerouslySetInnerHTML={{ __html: e.rfiTargetLabel(r) }} />
                  </div>
                  <div className="rfi-card-foot">
                    <span>
                      By <b>{r.requestedBy}</b> · {e.fmtDate(r.requestedAt)}
                    </span>
                    <span>{r.status === "closed" ? `Closed ${e.fmtDate(r.closedAt)}` : `Wanted ${r.requestedFor ? e.fmtDate(r.requestedFor) : "—"}`}</span>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty-state">
              <div className="es-ico">📋</div>
              <div className="es-t">
                No {filt === "all" ? "RFIs raised yet" : filt === "open" ? "open RFIs" : `${filt} RFIs`}
              </div>
              <div className="es-s">
                {filt === "open"
                  ? "All work has been signed off, or no contractor has requested inspection yet."
                  : "Switch the filter to see other RFIs, or raise a new one."}
              </div>
            </div>
          )}
        </div>
        <div className="rfi-detail-wrap">
          {e.state.rfiNew ? (
            <NewRfiForm onDone={(id) => { if (id) select(id); else { e.state.rfiNew = false; bump(); } }} />
          ) : sel ? (
            <RfiDetail key={sel.id} sel={sel} bump={bump} setView={setView} />
          ) : (
            <div className="rfi-empty">
              <div className="es-ico" style={{ fontSize: 32 }}>
                📋
              </div>
              <div className="es-t">Select an RFI</div>
              <div className="es-s">Pick a request from the list to see details and take action, or raise a new one.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RfiDetail({ sel, bump, setView }: { sel: any; bump: () => void; setView: (v: any) => void }) {
  const e = eng();
  const st = e.RFI_STATUS[sel.status];
  const oc = sel.outcome ? e.RFI_OUTCOME[sel.outcome] : null;
  const suggested = e.rfiSuggestedOutcome(sel);
  const [assign, setAssign] = useState(sel.assignedTo || "Catalin Radu");
  const [schedFor, setSchedFor] = useState(sel.requestedFor ? sel.requestedFor.slice(0, 16) : "");

  const schedule = () => {
    e.pushHistory();
    e.Store.scheduleRfi(sel.id, assign, schedFor ? new Date(schedFor).toISOString() : null);
    e.saveToStorage();
    bump();
    e.toast(`${sel.id} scheduled · ${assign}`, "success");
  };
  const start = () => {
    e.rfiOpenInspection(sel.id);
    e.saveToStorage();
    setView("qc");
  };
  const close = (outcome: string) => {
    const label = e.RFI_OUTCOME[outcome]?.label || outcome;
    if (!confirm(`Close ${sel.id} as "${label}"?`)) return;
    e.pushHistory();
    e.Store.closeRfi(sel.id, outcome);
    e.saveToStorage();
    bump();
    e.toast(`${sel.id} closed · ${label}`, outcome === "rejected" ? "warn" : "success");
  };
  const reopen = () => {
    if (!confirm(`Re-open ${sel.id}?`)) return;
    e.pushHistory();
    e.Store.reopenRfi(sel.id);
    e.saveToStorage();
    bump();
    e.toast(`${sel.id} re-opened`, "info");
  };
  const del = () => {
    if (!confirm(`Cancel and delete ${sel.id}? This cannot be undone via the gear menu.`)) return;
    e.pushHistory();
    e.Store.deleteRfi(sel.id);
    e.state.rfiSelected = null;
    e.saveToStorage();
    bump();
    e.toast(`${sel.id} cancelled`, "warn");
  };

  return (
    <div className="rfi-detail">
      <div className="rfi-dhead">
        <div>
          <div className="rfi-detail-id mono">{sel.id}</div>
          <h2>{sel.title}</h2>
        </div>
        <span className="rfi-status big" style={{ ["--rc" as any]: st.color }}>
          {st.glyph} {st.label}
        </span>
      </div>
      <div className="rfi-dbody">
        <KV k="Scope" v={scopeLabel(sel.scope)} />
        <KV k="Target" v={<span dangerouslySetInnerHTML={{ __html: e.rfiTargetLabel(sel) }} />} />
        <KV k="Requested by" v={sel.requestedBy} />
        <KV k="Raised" v={`${e.fmtDate(sel.requestedAt)} · ${e.fmtTime(sel.requestedAt)}`} />
        {sel.requestedFor ? <KV k="Wanted by" v={`${e.fmtDate(sel.requestedFor)} · ${e.fmtTime(sel.requestedFor)}`} /> : null}
        {sel.notes ? (
          <div className="rfi-notes">
            <div className="field-l">Notes</div>
            <div>{sel.notes}</div>
          </div>
        ) : null}

        {sel.status === "pending" && (
          <>
            <div className="rfi-section-title">Schedule this inspection</div>
            <div className="rfi-row">
              <label className="field-l">Assign to</label>
              <select className="rfi-input" value={assign} onChange={(ev) => setAssign(ev.target.value)}>
                <option>Catalin Radu</option>
              </select>
            </div>
            <div className="rfi-row">
              <label className="field-l">Scheduled for</label>
              <input className="rfi-input" type="datetime-local" value={schedFor} onChange={(ev) => setSchedFor(ev.target.value)} />
            </div>
            <div className="rfi-actions-row">
              <button className="obtn ok" onClick={schedule}>
                Schedule
              </button>
              <button className="obtn nu" onClick={del}>
                Cancel RFI
              </button>
            </div>
          </>
        )}
        {sel.status === "scheduled" && (
          <>
            <div className="rfi-section-title">Inspection scheduled</div>
            <KV k="Assigned to" v={sel.assignedTo || "—"} />
            <KV k="Scheduled" v={`${e.fmtDate(sel.scheduledFor)} · ${e.fmtTime(sel.scheduledFor)}`} />
            <div className="rfi-actions-row">
              <button className="obtn ok" onClick={start}>
                Start inspection →
              </button>
              <button className="obtn nu" onClick={reopen}>
                Move back to pending
              </button>
            </div>
          </>
        )}
        {sel.status === "in_progress" && (
          <>
            <div className="rfi-section-title">Inspection in progress</div>
            <KV k="Inspector" v={sel.assignedTo || "—"} />
            <div className="rfi-hint">
              {suggested ? (
                <>
                  Based on current results, the suggested outcome is{" "}
                  <b style={{ color: e.RFI_OUTCOME[suggested].color }}>{e.RFI_OUTCOME[suggested].label}</b>.
                </>
              ) : (
                "No results yet — open the inspection to fill the checklist."
              )}
            </div>
            <div className="rfi-actions-row">
              <button className="obtn" onClick={start}>
                Continue inspection →
              </button>
            </div>
            <div className="rfi-section-title">Close the inspection</div>
            <div className="rfi-actions-row" style={{ flexWrap: "wrap" }}>
              <button className="obtn ok" onClick={() => close("accepted")}>
                ✓ Accept
              </button>
              <button className="obtn" style={{ background: "#B8862B", color: "#fff" }} onClick={() => close("accepted_with_punch")}>
                ⚠ Accept with punch
              </button>
              <button className="obtn no" onClick={() => close("rejected")}>
                ✕ Reject
              </button>
            </div>
          </>
        )}
        {sel.status === "closed" && (
          <>
            <div className="rfi-section-title">Closed</div>
            <KV k="Outcome" v={<span style={{ color: oc ? oc.color : "inherit" }}>{oc ? oc.label : "—"}</span>} />
            <KV k="Closed" v={`${e.fmtDate(sel.closedAt)} · ${e.fmtTime(sel.closedAt)}`} />
            <KV k="Inspector" v={sel.assignedTo || "—"} />
            <div className="rfi-actions-row">
              <button className="obtn" onClick={start}>
                Open inspection record →
              </button>
              <button className="obtn nu" onClick={reopen}>
                Re-open
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function NewRfiForm({ onDone }: { onDone: (id: string | null) => void }) {
  const e = eng();
  const D = e.D;
  const [sc, setSc] = useState<string>(e.state.rfiNewScope || "table");
  const [f, setF] = useState({
    field: e.state.qcField || D.fields[0].id,
    row: "1",
    table: "1",
    station: D.stations[0].id,
    invs: "",
    title: "",
    notes: "",
    by: "Conecon GmbH",
    forV: "",
  });
  const upd = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));
  const setScope = (s: string) => {
    setSc(s);
    e.state.rfiNewScope = s;
  };

  const submit = () => {
    if (!guardCloseout()) return;
    if (!f.title.trim()) {
      e.toast("Title is required", "warn");
      return;
    }
    let target: any = null;
    if (sc === "table") {
      target = { fid: f.field, row: parseInt(f.row || "1", 10), t: parseInt(f.table || "1", 10) };
    } else if (sc === "station") {
      const invIdx: number[] = [];
      (f.invs || "1").split(",").forEach((piece) => {
        const m = piece.trim().match(/^(\d+)\s*-\s*(\d+)$/);
        if (m) {
          const a = +m[1];
          const b = +m[2];
          for (let i = Math.min(a, b); i <= Math.max(a, b); i++) invIdx.push(i - 1);
        } else {
          const n = parseInt(piece, 10);
          if (!isNaN(n)) invIdx.push(n - 1);
        }
      });
      target = { stationId: f.station, invIdx: [...new Set(invIdx)].sort((a, b) => a - b) };
    }
    e.pushHistory();
    const id = e.Store.createRfi({
      scope: sc,
      target,
      title: f.title.trim(),
      notes: f.notes.trim(),
      requestedBy: f.by,
      requestedFor: f.forV ? new Date(f.forV).toISOString() : null,
    });
    e.state.rfiNew = false;
    e.saveToStorage();
    e.toast(`${id} raised`, "success");
    onRfiCreated(id, f.title.trim());
    onDone(id);
  };

  return (
    <div className="rfi-detail">
      <div className="rfi-dhead">
        <h2>New Request for Inspection</h2>
        <button className="obtn" onClick={() => onDone(null)}>
          Cancel
        </button>
      </div>
      <div className="rfi-dbody">
        <div className="rfi-row">
          <label className="field-l">Scope</label>
          <div className="rfi-scope-pick">
            {["table", "station", "site"].map((s) => (
              <button key={s} className={`rfi-scope-btn ${sc === s ? "sel" : ""}`} onClick={() => setScope(s)}>
                {scopeLabel(s)}
              </button>
            ))}
          </div>
        </div>
        {sc === "table" && (
          <>
            <div className="rfi-row">
              <label className="field-l">Field</label>
              <select className="rfi-input" value={f.field} onChange={(ev) => upd("field", ev.target.value)}>
                {D.fields.map((fl: any) => (
                  <option key={fl.id} value={fl.id}>
                    {fl.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="rfi-row">
              <label className="field-l">Row</label>
              <input className="rfi-input" type="number" min={1} max={20} value={f.row} onChange={(ev) => upd("row", ev.target.value)} />
            </div>
            <div className="rfi-row">
              <label className="field-l">Table</label>
              <input className="rfi-input" type="number" min={1} max={65} value={f.table} onChange={(ev) => upd("table", ev.target.value)} />
            </div>
          </>
        )}
        {sc === "station" && (
          <>
            <div className="rfi-row">
              <label className="field-l">Station</label>
              <select className="rfi-input" value={f.station} onChange={(ev) => upd("station", ev.target.value)}>
                {D.stations.map((s: any) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="rfi-row">
              <label className="field-l">Inverters</label>
              <input className="rfi-input" type="text" placeholder="e.g. 1-4 or 1,3,5" value={f.invs} onChange={(ev) => upd("invs", ev.target.value)} />
            </div>
          </>
        )}
        {sc === "site" && (
          <div className="rfi-row">
            <div className="rfi-hint">Site-wide RFIs cover all civil &amp; infrastructure work — no target needed.</div>
          </div>
        )}
        <div className="rfi-row">
          <label className="field-l">Title</label>
          <input className="rfi-input" type="text" maxLength={120} placeholder="What work is ready for inspection?" value={f.title} onChange={(ev) => upd("title", ev.target.value)} />
        </div>
        <div className="rfi-row">
          <label className="field-l">Notes for inspector</label>
          <textarea className="rfi-input" rows={3} maxLength={500} placeholder="Any context, attachments, special access requirements…" value={f.notes} onChange={(ev) => upd("notes", ev.target.value)} />
        </div>
        <div className="rfi-row">
          <label className="field-l">Requested by</label>
          <input className="rfi-input" type="text" value={f.by} onChange={(ev) => upd("by", ev.target.value)} />
        </div>
        <div className="rfi-row">
          <label className="field-l">Wanted by</label>
          <input className="rfi-input" type="datetime-local" value={f.forV} onChange={(ev) => upd("forV", ev.target.value)} />
        </div>
        <div className="rfi-actions-row">
          <button className="obtn ok" onClick={submit}>
            Raise RFI
          </button>
        </div>
      </div>
    </div>
  );
}

function KV({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="rfi-kv">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}
